import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  IconEdit, IconEye, IconMailForward, IconTrash,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { Button } from '@mantine/core';
import prepareTranslate from '@/helpers/dictionary';
import { shortenText, stripHtmlTags } from '@/helpers/string';
import { getCurrentUserFullName } from '@/helpers/user';
import agentMutations from '@/api/mutitations/agent';
import emailMutations from '@/api/mutitations/email';
import s3Mutations from '@/api/mutitations/s3';
import Modal from '@/components/Modal/Index';
import Table from '../Table';
import { extractFilterOptions, filterAndSortData } from '../Table/Table.functions';
import ViewAgentEmail from './ViewAgentEmail';
import EditAgentEmail from './EditAgentEmail';
import DeleteAgentEmail from './DeleteAgentEmail';
import ResendAgentEmail from './ResendAgentEmail';

const EMAILS_KEY = 'emails';

/**
 * Orders the log as a history: emails are grouped by thread (an original send
 * and all of its resends) and each thread is ordered chronologically.
 */
const sortAsHistory = (emails) => {
  const threadDate = emails.reduce((acc, email) => {
    const threadId = email.originalEmailId || email._id;
    const date = new Date(email.date).getTime();
    acc[threadId] = Math.min(acc[threadId] ?? date, date);
    return acc;
  }, {});

  return [...emails].sort((a, b) => {
    const aThread = a.originalEmailId || a._id;
    const bThread = b.originalEmailId || b._id;

    if (aThread !== bThread) {
      return threadDate[bThread] - threadDate[aThread];
    }

    return new Date(a.date) - new Date(b.date);
  });
};

const ViewAgentEmails = (props) => {
  const {
    agent,
    id,
    setKey,
  } = props;

  const t = prepareTranslate();

  const [emails, setEmails] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const [viewOpened, { open: openView, close: closeView }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [resendOpened, { open: openResend, close: closeResend }] = useDisclosure(false);
  const [resultOpened, { open: openResult, close: closeResult }] = useDisclosure(false);

  const { mutateAsync: updateAgentItem } = agentMutations.useUpdateAgentItem();
  const { mutateAsync: sendEmail } = emailMutations.useSendEmail();
  const { mutateAsync: uploadFiles } = s3Mutations.useUpload();

  const agentId = id || agent?._id;

  const cleanAndShortenHtmlText = (htmlString, maxLength = 100) => {
    const content = stripHtmlTags(htmlString);
    const shortText = shortenText(content, maxLength);
    return shortText;
  };

  useEffect(() => {
    if (!agent?.emails) {
      setEmails([]);
      return;
    }

    const activeEmails = agent.emails.filter((email) => email.status !== 'deleted');

    const cleanedEmails = sortAsHistory(activeEmails).map((email) => ({
      ...email,
      cleanContent: cleanAndShortenHtmlText(email.content, 50),
      // Only a resend is labelled - an original send needs no explanation.
      sendTypeLabel: email.sendType === 'resend'
        ? t('components.viewAgentEmails.sendTypes.resend')
        : '',
    }));

    setEmails(cleanedEmails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  const globalColumnsProps = {
    isVisible: true,
    isFilterActive: true,
    sortable: true,
  };

  const columns = [
    {
      title: t('components.viewAgentEmails.columns.name'),
      accessor: 'name',
      ...globalColumnsProps,
    },
    {
      title: t('components.viewAgentEmails.columns.date'),
      accessor: 'date',
      filterType: 'datePicker',
      transforms: [{ func: 'date', params: { withTime: false } }],
      ...globalColumnsProps,
    },
    {
      title: t('components.viewAgentEmails.columns.sendType'),
      accessor: 'sendTypeLabel',
      ...globalColumnsProps,
    },
    {
      title: t('components.viewAgentEmails.columns.resendReason'),
      accessor: 'resendReason',
      ...globalColumnsProps,
    },
    {
      title: t('components.viewAgentEmails.columns.content'),
      accessor: 'cleanContent',
      ...globalColumnsProps,
    },
    {
      title: t('components.viewAgentEmails.columns.actions'),
      menuLabel: t('components.viewAgentEmails.columns.actions'),
      actions: [
        {
          title: t('components.viewAgentEmails.actions.view'),
          icon: <IconEye />,
          isPrimary: false,
          action: (_, row) => {
            setSelectedEmail(row);
            openView();
          },
        },
        {
          title: t('components.viewAgentEmails.actions.resend'),
          icon: <IconMailForward />,
          isPrimary: false,
          action: (_, row) => {
            setSelectedEmail(row);
            openResend();
          },
        },
        {
          title: t('components.viewAgentEmails.actions.edit'),
          icon: <IconEdit />,
          isPrimary: false,
          action: (_, row) => {
            setSelectedEmail(row);
            openEdit();
          },
        },
        {
          title: t('components.viewAgentEmails.actions.delete'),
          icon: <IconTrash />,
          isPrimary: false,
          action: (_, row) => {
            setSelectedEmail(row);
            openDelete();
          },
        },
      ],
    },
  ];

  useEffect(() => {
    const options = extractFilterOptions(emails, columns);
    setFilterOptions(options);

    const initialFilters = columns
      .filter((col) => col.accessor)
      .reduce((acc, col) => {
        acc[col.accessor] = '';
        return acc;
      }, {});
    setColumnFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emails]);

  const getData = async (start, end, query, sort) => {
    if (!emails || !Array.isArray(emails)) {
      return { data: [], count: 0 };
    }

    const newFilterOptions = extractFilterOptions(emails, columns);
    setFilterOptions(newFilterOptions);
    const filteredAndSorted = filterAndSortData(emails, query, sort);

    return { data: filteredAndSorted, count: filteredAndSorted.length };
  };

  const refreshAgent = () => {
    if (setKey) setKey((prev) => prev + 1);
  };

  const showResult = (label) => {
    setResultLabel(label);
    openResult();
  };

  /**
   * Files dropped into the editor are uploaded to S3 so the record keeps
   * pointing at real files. Files removed from the record are left in S3 on
   * purpose - a resend row can still reference the same file.
   */
  const uploadNewFiles = async (newFiles) => {
    if (!newFiles?.length) return [];

    const formData = new FormData();
    newFiles.forEach((file) => formData.append('files', file.file, file.name));

    const response = await uploadFiles(formData);
    return response?.files || [];
  };

  const handleEditSave = async (updatedEmail) => {
    const {
      name, content, keptFiles, newFiles,
    } = updatedEmail;

    setLoading(true);
    try {
      const uploadedFiles = await uploadNewFiles(newFiles);

      await updateAgentItem({
        agentId,
        itemId: selectedEmail._id,
        key: EMAILS_KEY,
        data: {
          name,
          content,
          files: [...keptFiles, ...uploadedFiles],
          editedAt: new Date(),
          editedBy: getCurrentUserFullName(),
        },
      });

      closeEdit();
      refreshAgent();
      showResult(t('components.viewAgentEmails.edit.success'));
    } catch (error) {
      showResult(t('components.viewAgentEmails.edit.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (deletionReason) => {
    setLoading(true);
    try {
      await updateAgentItem({
        agentId,
        itemId: selectedEmail._id,
        key: EMAILS_KEY,
        data: {
          status: 'deleted',
          deletionReason,
          deletedAt: new Date(),
          deletedBy: getCurrentUserFullName(),
        },
      });

      closeDelete();
      refreshAgent();
      showResult(t('components.viewAgentEmails.delete.success'));
    } catch (error) {
      showResult(t('components.viewAgentEmails.delete.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (resendData) => {
    const {
      subject,
      content,
      recipients,
      resendReason,
      keptFiles,
      newFiles,
    } = resendData;

    setLoading(true);
    try {
      const formData = new FormData();

      recipients.forEach((recipient) => formData.append('recipients', recipient));
      formData.append('subject', subject);
      formData.append('content', content);
      formData.append('sentBy', getCurrentUserFullName());
      formData.append('originalEmailId', selectedEmail.originalEmailId || selectedEmail._id);
      formData.append('resendReason', resendReason);

      keptFiles.forEach((file) => {
        formData.append('existingFiles', JSON.stringify({ name: file.name, path: file.path }));
      });

      newFiles.forEach((file) => {
        formData.append('attachment', new File([file.fileData], file.name));
      });

      await sendEmail(formData);

      closeResend();
      refreshAgent();
      showResult(t('components.viewAgentEmails.resend.success'));
    } catch (error) {
      showResult(t('components.viewAgentEmails.resend.error'));
    } finally {
      setLoading(false);
    }
  };

  const resultActions = [
    <Button key='close' onClick={closeResult}>
      {t('global.close')}
    </Button>,
  ];

  return (
    <>
        <Table
          key={emails.length}
          isExportActive={false}
          columns={columns}
          getData={getData}
          isColumnsActive={false}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          filterOptions={filterOptions}
        />

      <ViewAgentEmail
        open={openView}
        close={closeView}
        opened={viewOpened}
        email={selectedEmail}
      />

      <EditAgentEmail
        opened={editOpened}
        close={closeEdit}
        email={selectedEmail}
        onSave={handleEditSave}
        loading={loading}
      />

      <DeleteAgentEmail
        opened={deleteOpened}
        close={closeDelete}
        email={selectedEmail}
        onDelete={handleDelete}
        loading={loading}
      />

      <ResendAgentEmail
        opened={resendOpened}
        close={closeResend}
        email={selectedEmail}
        agentEmail={agent?.personalDetails?.email}
        onSend={handleResend}
        loading={loading}
      />

      <Modal
        opened={resultOpened}
        onClose={closeResult}
        label={resultLabel}
        actions={resultActions}
      />
    </>
  );
};

ViewAgentEmails.propTypes = {
  agent: PropTypes.object,
  id: PropTypes.string,
  setKey: PropTypes.func,
};

export default ViewAgentEmails;
